import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useEnhancedCart } from "@/contexts/EnhancedCartContext";
import { useCustomerAuth } from "@/contexts/CustomerAuthContext";
import { CustomerAuthModal } from "@/components/storefront/CustomerAuthModal";
import { DeliveryMethodSelect } from "@/components/checkout/DeliveryMethodSelect";
import { DeliveryLocationSelect } from "@/components/checkout/DeliveryLocationSelect";
import {
  PaymentMethodSelect,
  PaymentMethod,
} from "@/components/checkout/PaymentMethodSelect";
import { BankAccount } from "@/contexts/SettingsContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  ShoppingBag,
  Loader,
  MessageCircle,
  CreditCard,
} from "lucide-react";
import api from "@/utils/api";
import { getCartLineSubtotal, getEffectiveUnitPrice } from "@/utils/wholesalePricing";
import { CheckoutLoader } from "@/components/storefront/StorefrontLoaders";
import { trackSalesEvent } from "@/lib/sales-events";

interface DeliveryLocation {
  name: string;
  price: number;
}

interface StoreCheckoutData {
  business: {
    name: string;
    currency: string;
    whatsappNumber?: string;
  };
  checkoutOptions: {
    productDelivery?: {
      enableDelivery?: boolean;
      enablePickup?: boolean;
      deliveryLocations?: DeliveryLocation[];
    };
    paymentOptions?: {
      bankTransfer?: {
        enabled?: boolean;
        accountInfo?: string;
        accounts?: BankAccount[];
      };
      cashOnDelivery?: { enabled?: boolean };
      manualPayment?: { enabled?: boolean; instructions?: string };
    };
    guestCheckout?: {
      enabled?: boolean;
      autoSaveCustomer?: boolean;
      requireAccount?: boolean;
    };
  };
}

export function EnhancedCheckoutPage({
  goBack,
  primaryColor = "#3b82f6",
}: {
  goBack: () => void;
  primaryColor?: string;
}) {
  const navigate = useNavigate();
  const { storeUrl } = useParams<{ storeUrl: string }>();
  const { items, getTotal, clearCart } = useEnhancedCart();

  useEffect(() => {
    trackSalesEvent(storeUrl, "checkout");
  }, [storeUrl]);
  const { customer, isAuthenticated } = useCustomerAuth();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [storeData, setStoreData] = useState<StoreCheckoutData | null>(null);
  const [loading, setLoading] = useState(true);

  // Customer information
  const [customerInfo, setCustomerInfo] = useState({
    name: customer?.name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    address: customer?.address || "",
  });

  // Checkout selections
  const [deliveryMethod, setDeliveryMethod] = useState<"delivery" | "pickup">(
    "pickup"
  );
  const [selectedLocation, setSelectedLocation] =
    useState<DeliveryLocation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod | "">("");
  const [paymentDetails, setPaymentDetails] = useState<{
    proofOfPayment?: File;
    notes?: string;
  }>({});
  const [orderNotes, setOrderNotes] = useState("");

  useEffect(() => {
    if (storeUrl) {
      fetchStoreCheckoutData();
    }
  }, [storeUrl]);

  useEffect(() => {
    // Update customer info when customer data changes
    if (customer) {
      setCustomerInfo({
        name: customer.name || "",
        email: customer.email || "",
        phone: customer.phone || "",
        address: customer.address || "",
      });
    }
  }, [customer]);

  const fetchStoreCheckoutData = async () => {
    try {
      setLoading(true);
      const response = await api.get(
        `/api/public/stores/${storeUrl}/checkout-options`
      );
      setStoreData(response.data);
    } catch (error) {
      console.error("Error fetching store checkout data:", error);
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const subtotal = getTotal();
  const deliveryFee = selectedLocation?.price || 0;
  const total = subtotal + deliveryFee;

  // Format price
  const formatPrice = (price: number) => {
    return price.toLocaleString("en-NG", {
      style: "currency",
      currency: storeData?.business?.currency || "NGN",
    });
  };

  const handleCustomerInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCustomerInfo((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDeliveryMethodSelect = (method: "delivery" | "pickup") => {
    setDeliveryMethod(method);
    if (method === "pickup") {
      setSelectedLocation(null);
    }
  };

  const handleLocationSelect = (location: DeliveryLocation | null) => {
    setSelectedLocation(location);
  };

  const handlePaymentMethodSelect = (
    method: PaymentMethod,
    details?: { proofOfPayment?: File; notes?: string }
  ) => {
    setPaymentMethod(method);
    if (details) {
      setPaymentDetails(details);
    }
  };

  const openWhatsAppWithMessage = (message: string) => {
    const whatsappNumber = storeData?.business?.whatsappNumber;
    if (!whatsappNumber) return;

    const encoded = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber.replace(
      /[^\d]/g,
      ""
    )}?text=${encoded}`;

    // Try opening in a new tab/window first
    const newWindow = window.open(whatsappUrl, "_blank");

    // If the popup was blocked, fall back to same-tab navigation
    if (!newWindow) {
      window.location.href = whatsappUrl;
    }
  };

  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();

    if (items.length === 0) {
      return;
    }

    // Basic form validation
    if (!customerInfo.name || !customerInfo.email || !customerInfo.phone) {
      alert("Please fill in all required fields");
      return;
    }

    // Check if account is required but user is not authenticated
    if (
      storeData?.checkoutOptions?.guestCheckout?.requireAccount &&
      !isAuthenticated
    ) {
      alert("Account login is required to complete checkout");
      setShowAuthModal(true);
      return;
    }

    const hasPresetLocations =
      (storeData?.checkoutOptions?.productDelivery?.deliveryLocations || [])
        .length > 0;
    if (
      deliveryMethod === "delivery" &&
      hasPresetLocations &&
      !selectedLocation
    ) {
      alert("Please select a delivery location");
      return;
    }

    if (!paymentMethod) {
      alert("Please select a payment method");
      return;
    }

    // For bank transfer, proof of payment is required
    if (paymentMethod === "bankTransfer" && !paymentDetails.proofOfPayment) {
      alert("Please upload proof of payment");
      return;
    }

    try {
      setIsSubmitting(true);

      // Create form data for file upload
      const formData = new FormData();

      // Add basic order info
      const orderData = {
        customer: customerInfo,
        items: items.map((item) => ({
          productId: item.productId,
          variantId: item.variantId,
          variantName: item.variantName,
          variantAttributes: item.variantAttributes,
          quantity: item.quantity,
          price: getEffectiveUnitPrice(
            item.price,
            item.quantity,
            item.wholesale
          ),
          options: item.options,
        })),
        delivery: {
          method: deliveryMethod,
          location: selectedLocation,
          fee: deliveryFee,
        },
        payment: {
          method: paymentMethod,
          notes: paymentDetails.notes || "",
        },
        subtotal,
        total,
        notes: orderNotes,
        isGuestOrder: !isAuthenticated,
      };

      formData.append("orderData", JSON.stringify(orderData));

      // Add payment proof if available
      if (paymentDetails.proofOfPayment) {
        formData.append("paymentProof", paymentDetails.proofOfPayment);
      }

      // Submit order
      const response = await api.post("/api/orders", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          "store-url": storeUrl,
        },
      });

      // Order completed successfully
      if (response.data?.success && response.data?.order) {
        const createdOrder = response.data.order;

        // If WhatsApp checkout, open WhatsApp with immutable invoice link
        if (paymentMethod === "whatsapp" && createdOrder.invoiceUrl) {
          const message = `🧾 Order #${
            createdOrder.orderNumber
          }\nTotal: ${formatPrice(createdOrder.total)}\n\nView invoice: ${
            createdOrder.invoiceUrl
          }`;
          openWhatsAppWithMessage(message);
        }

        // Clear cart
        clearCart();
        trackSalesEvent(storeUrl, "purchase");

        // Redirect to order confirmation
        navigate(`/${storeUrl}/order-confirmation/${createdOrder._id}`);
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      alert("There was an error processing your order. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return <CheckoutLoader />;
  }

  // If cart is empty, redirect to store
  if (items.length === 0) {
    return (
      <div className="container max-w-5xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <ShoppingBag className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Your cart is empty</h2>
          <p className="text-muted-foreground mb-8">
            Add items to your cart to checkout
          </p>
          <Button onClick={() => navigate(`/${storeUrl}`)}>
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-transparent">
        <div className="mx-auto max-w-5xl">
          <div className="mb-8 flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={goBack}
              className="rounded-full border-black/10 text-black/70 hover:!bg-black/[0.04] hover:!text-black"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="font-display text-2xl font-black tracking-tight text-black">
                Checkout
              </h1>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6 items-start">
            <div className="space-y-4">
              {/* Customer Information */}
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0">1</span>
                    <div>
                      <CardTitle className="text-base">Customer Information</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        Enter your contact and delivery details
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-sm">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={customerInfo.name}
                        onChange={handleCustomerInfoChange}
                        required
                        placeholder="John Doe"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="email" className="text-sm">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={customerInfo.email}
                        onChange={handleCustomerInfoChange}
                        required
                        placeholder="you@example.com"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label htmlFor="phone" className="text-sm">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={customerInfo.phone}
                        onChange={handleCustomerInfoChange}
                        required
                        placeholder="+234 800 000 0000"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label htmlFor="address" className="text-sm">Address</Label>
                      <Input
                        id="address"
                        name="address"
                        value={customerInfo.address}
                        onChange={handleCustomerInfoChange}
                        placeholder="Optional"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Delivery Options */}
              {storeData?.checkoutOptions && (
                <Card className="border border-gray-200 bg-white">
                  <CardHeader className="pb-4">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0">2</span>
                      <div>
                        <CardTitle className="text-base">Delivery Options</CardTitle>
                        <CardDescription className="text-xs mt-0.5">
                          Choose how you want to receive your order
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <DeliveryMethodSelect
                      onSelectMethod={handleDeliveryMethodSelect}
                      enableDelivery={
                        storeData.checkoutOptions.productDelivery?.enableDelivery
                      }
                      enablePickup={
                        storeData.checkoutOptions.productDelivery?.enablePickup
                      }
                    />
                    {deliveryMethod === "delivery" && (
                      <DeliveryLocationSelect
                        currency={storeData?.business?.currency}
                        onSelectLocation={handleLocationSelect}
                        locations={
                          storeData.checkoutOptions.productDelivery
                            ?.deliveryLocations || []
                        }
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Payment Options */}
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0">3</span>
                    <div>
                      <CardTitle className="text-base">Payment Method</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        Select how you want to pay for your order
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <PaymentMethodSelect
                    onSelectMethod={handlePaymentMethodSelect}
                    paymentOptions={storeData?.checkoutOptions?.paymentOptions}
                    showWhatsApp={
                      Boolean(storeData?.business?.whatsappNumber) &&
                      Boolean(storeData?.checkoutOptions?.whatsapp?.enabled)
                    }
                  />
                </CardContent>
              </Card>

              {/* Order Notes */}
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-4">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center justify-center w-6 h-6 rounded-full bg-foreground text-background text-xs font-semibold shrink-0">4</span>
                    <div>
                      <CardTitle className="text-base">Order Notes</CardTitle>
                      <CardDescription className="text-xs mt-0.5">
                        Any special instructions? (optional)
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)}
                    placeholder="Any special instructions for your order..."
                    className="resize-none"
                    rows={3}
                  />
                </CardContent>
              </Card>
            </div>

            {/* Order Summary, sticky on desktop */}
            <div className="space-y-4 lg:sticky lg:top-6">
              <Card className="border border-gray-200 bg-white">
                <CardHeader className="pb-3 border-b border-gray-100">
                  <CardTitle className="text-base">Order Summary</CardTitle>
                </CardHeader>
                <CardContent className="pt-4 space-y-4">
                  <div className="space-y-3">
                    {items.map((item) => (
                      <div
                        key={`${item.productId}-${item.variantId || ""}-${JSON.stringify(item.options)}`}
                        className="flex gap-3"
                      >
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-14 h-14 object-cover rounded-md shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{item.name}</p>
                          {item.variantName && (
                            <p className="text-xs text-muted-foreground">{item.variantName}</p>
                          )}
                          {Object.entries(item.options || {}).map(([key, value]) => (
                            <p key={key} className="text-xs text-muted-foreground">
                              {key}: {value}
                            </p>
                          ))}
                          <div className="flex justify-between items-center mt-1">
                            <span className="text-xs text-muted-foreground">Qty {item.quantity}</span>
                            <span className="text-sm font-medium">
                              {formatPrice(getCartLineSubtotal(item))}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <Separator />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between text-muted-foreground">
                      <span>Subtotal</span>
                      <span className="text-foreground">{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-muted-foreground">
                      <span>Delivery</span>
                      <span className="text-foreground">
                        {deliveryMethod === "delivery" && selectedLocation
                          ? formatPrice(deliveryFee)
                          : "Free"}
                      </span>
                    </div>
                    <Separator />
                    <div className="flex justify-between font-semibold text-base pt-1">
                      <span>Total</span>
                      <span>{formatPrice(total)}</span>
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button
                    size="lg"
                    onClick={handleSubmitOrder}
                    className="w-full rounded-full font-semibold text-white hover:!text-white hover:opacity-90"
                    style={{ backgroundColor: primaryColor }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader className="mr-2 h-4 w-4 animate-spin" />
                        Processing...
                      </>
                    ) : paymentMethod === "whatsapp" ? (
                      <>
                        <MessageCircle className="mr-2 h-4 w-4" />
                        Order via WhatsApp
                      </>
                    ) : (
                      <>
                        <CreditCard className="mr-2 h-4 w-4" />
                        Place Order
                      </>
                    )}
                  </Button>
                </CardFooter>
              </Card>
              <p className="text-xs text-center text-muted-foreground px-2">
                By placing your order, you agree to our Terms of Service and Privacy Policy.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Customer Auth Modal */}
      <CustomerAuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        storeUrl={storeUrl || ""}
      />
    </>
  );
}
